/**
 * Sweeps a striking segment against every other fighter's volumes and
 * resolves the first contact: block, check, parry, or a hit whose damage
 * follows from closing speed, effective mass and the part hit. No damage table.
 */
import { closestPointsSegments } from "../math/geometry.ts";
import {
  add,
  clampLength,
  dot,
  length,
  normalize,
  scale,
  sub,
  type Vec3,
  v3,
} from "../math/vec.ts";
import { PART_NAMES, Part, poseFighter, type Volume } from "../skeleton.ts";
import type { FighterState, MatchState, StrikeState } from "../state.ts";
import { DT, tuning } from "../tuning.ts";
import type { HitEvent, SimEvent } from "./events.ts";
import { inWindup, limbFactors, openAfterMiss } from "./injuries.ts";
import { poseSeen, rewindTicks } from "./lag.ts";
import { armIntact, leadLegSide } from "./movement.ts";
import {
  chainMass,
  deflectStrike,
  limbKind,
  limbReach,
  limbRoot,
  strikeCost,
  toLocal,
  toWorld,
} from "./strikes.ts";

/** How much of a hit a body shrugs off: 1 balanced, more for a stronger build (design §4.5). */
export function toughness(f: FighterState): number {
  return 1 + (f.build.strength - 1) * tuning.build.toughness;
}

/** Which arm a part belongs to (0 left, 1 right), or null for anything else. */
function armOf(part: number): 0 | 1 | null {
  if (part === Part.UpperArmL || part === Part.ForearmL) return 0;
  if (part === Part.UpperArmR || part === Part.ForearmR) return 1;
  return null;
}

/** Which leg a part belongs to (0 left, 1 right), or null. */
function legOf(part: number): 0 | 1 | null {
  if (part === Part.ThighL || part === Part.ShinL) return 0;
  if (part === Part.ThighR || part === Part.ShinR) return 1;
  return null;
}

/**
 * Whether a part is guarding right now: either segment of a covering arm (a
 * block on the upper arm is a block, which is how an elbow stops a body kick),
 * or the lead leg while it checks.
 */
function guardKind(target: FighterState, part: number): "arm" | "check" | null {
  const arm = armOf(part);
  if (arm !== null && target.guard && armIntact(target, arm)) return "arm";
  const leg = legOf(part);
  if (leg !== null && target.checking && leg === leadLegSide(target)) return "check";
  return null;
}

const VULN: Record<string, number> = {
  head: tuning.vulnerability.head,
  torso: tuning.vulnerability.torso,
  upperArmL: tuning.vulnerability.upperArm,
  upperArmR: tuning.vulnerability.upperArm,
  forearmL: tuning.vulnerability.forearm,
  forearmR: tuning.vulnerability.forearm,
  thighL: tuning.vulnerability.thigh,
  thighR: tuning.vulnerability.thigh,
  shinL: tuning.vulnerability.shin,
  shinR: tuning.vulnerability.shin,
};

interface Candidate {
  target: FighterState;
  /** The target's frame as the attacker saw it (lag compensation). */
  frame: { pos: Vec3; facing: number };
  vol: Volume;
  /** Where along the sweep (0..1) the tip first touches the volume. */
  entry: number;
}

/** Returns true when the strike is consumed (hit, blocked or parried). */
export function sweepStrike(
  state: MatchState,
  attacker: FighterState,
  strike: StrikeState,
  tipPrev: Vec3,
  tipNow: Vec3,
  radius: number,
  events: SimEvent[],
): boolean {
  const sweep = sub(tipNow, tipPrev);
  const sweepLen = length(sweep);
  const strikeDir = sweepLen > 1e-9 ? scale(sweep, 1 / sweepLen) : v3(0, 0, 1);
  // The first volume the tip enters takes the strike. Entry is found by backing
  // up from the closest approach to where the tip first touched the surface: a
  // sweep that ends inside both a forearm and the torso behind it met the
  // forearm first, whichever axis it ends nearer.
  let best: Candidate | null = null;
  const rewind = rewindTicks(state, attacker.lastInput.viewTick);
  for (const target of state.fighters) {
    if (target.id === attacker.id || !target.alive) continue;
    const seen = poseSeen(state, target, rewind);
    const vols = poseFighter(seen);
    for (const vol of vols) {
      const q = closestPointsSegments(tipPrev, tipNow, vol.a, vol.b);
      const rr = radius + vol.r;
      if (q.dist2 >= rr * rr) continue;
      const back = sweepLen > 1e-9 ? Math.sqrt(rr * rr - q.dist2) / sweepLen : 0;
      const entry = q.s - back > 0 ? q.s - back : 0;
      if (!best || entry < best.entry || (entry === best.entry && vol.part < best.vol.part)) {
        best = { target, frame: seen, vol, entry };
      }
    }
  }
  if (!best) return false;
  const target = best.target;
  const vol = best.vol;

  // Contact is resolved at the entry, with the surface normal there. A sweep
  // that passes through a capsule otherwise yields a normal perpendicular to the punch.
  const entry = add(tipPrev, scale(sweep, best.entry));
  const axisPoint = closestPointsSegments(entry, entry, vol.a, vol.b).c2;
  let n = sub(entry, axisPoint);
  n = length(n) > 1e-6 ? normalize(n) : scale(strikeDir, -1);
  // The sweep carries the whole body's motion; a strike can only put so much of
  // it behind the fist (a real step-in moves the body at 1–2 m/s at impact).
  const armVel = sub(scale(sweep, 1 / DT), attacker.vel);
  const tipVel = add(armVel, clampLength(attacker.vel, tuning.bodyCarry));
  const closing = dot(sub(tipVel, target.vel), scale(n, -1));
  if (closing < tuning.minClosingSpeed) return false;

  // Effective mass: limb mass × extension × body behind it × injuries.
  const factors = limbFactors(attacker, strike.limb);
  const root = toWorld(attacker, limbRoot(attacker, strike.limb));
  const extension = length(sub(entry, root)) / limbReach(strike.limb);
  const ext = tuning.extension[limbKind(strike.limb)][strike.arc];
  let extFactor = 1;
  let extLabel = "clean";
  if (extension < ext.jammedBelow) {
    extFactor = ext.jammedFactor;
    extLabel = "jammed";
  } else if (extension < ext.peakFrom) {
    // Building toward full extension: short of it, the strike carries part of its power.
    const k = (extension - ext.jammedBelow) / (ext.peakFrom - ext.jammedBelow);
    extFactor = ext.jammedFactor + (1 - ext.jammedFactor) * k;
    extLabel = "short";
  } else if (extension > ext.overReachAbove) {
    extFactor = ext.overReachFactor;
    extLabel = "overreach";
  }
  const bodyFactor = dot(attacker.vel, scale(n, -1)) > 0.5 ? tuning.bodyFactor : 1;
  const limbMass = strike.limb < 2 ? tuning.limbs.arm.mass : tuning.limbs.leg.mass;
  const chain = chainMass(strike.limb, strike.arc, strike.lead);
  // A stronger build puts more mass behind the same limb.
  const mEff = limbMass * extFactor * bodyFactor * factors.mass * chain * attacker.build.strength;
  const energy = 0.5 * mEff * closing * closing;
  // Counter-hit: caught loading, or caught recovering from a strike that missed or was blocked.
  const counter = inWindup(target) || openAfterMiss(target);
  const partName = PART_NAMES[vol.part] ?? "torso";
  let damage =
    (tuning.k * energy * (VULN[partName] ?? 1) * (counter ? tuning.counterHit : 1)) /
    toughness(target);

  const arm = armOf(vol.part);
  const guard = guardKind(target, vol.part);
  const parrying = arm !== null && target.parry !== null && target.parry.arm === arm;

  const event: HitEvent = {
    type: "hit",
    attacker: attacker.id,
    target: target.id,
    part: vol.part,
    at: toLocal(best.frame, entry),
    limb: strike.limb,
    arc: strike.arc,
    damage: 0,
    energy,
    closingSpeed: closing,
    extension: extLabel,
    bodyFactor,
    lead: strike.lead,
    chain,
    counter,
    blocked: false,
    checked: false,
    parried: false,
    absorb: 0,
    collapsed: false,
    recoil: 0,
  };

  if (parrying) {
    event.parried = true;
    attacker.stamina = Math.max(
      0,
      attacker.stamina -
        strikeCost(strike.limb, strike.charge, strike.lead) * tuning.parry.attackerJolt,
    );
    deflectStrike(attacker, strike);
    target.stamina = Math.min(tuning.stamina.max, target.stamina + tuning.parry.refund);
    target.parry = null;
    events.push(event);
    return true;
  }

  let impulseFraction = 1;
  if (guard) {
    const g = tuning.guard;
    const axis = normalize(sub(vol.b, vol.a));
    const squareness = 1 - Math.abs(dot(axis, strikeDir));
    const absorb = g.absorbBase + g.absorbSquare * squareness;
    event.blocked = true;
    event.checked = guard === "check";
    event.absorb = absorb;
    strike.blocked = true;
    if (target.exhausted && energy > g.collapseEnergy) {
      event.collapsed = true;
      target.stagger = tuning.staggerTicks;
    } else {
      target.stamina = Math.max(0, target.stamina - energy * absorb * g.staminaPerEnergy);
      // Arms blocking kicks wear fast (forearms break on kicks); a checking shin is built for it.
      const wear = guard === "arm" && strike.limb >= 2 ? g.kickWear : 1;
      damage = damage * (1 - absorb) * g.forearmWear * wear;
      impulseFraction = 1 - absorb;
      if (guard === "check" && strike.limb >= 2) {
        // Shin on shin: the kicker's own shin pays for a checked kick.
        const shin = strike.limb === 2 ? Part.ShinL : Part.ShinR;
        const recoil =
          Math.round(tuning.k * energy * tuning.vulnerability.shin * tuning.check.recoil * 10) / 10;
        attacker.hp[shin] = Math.max(0, (attacker.hp[shin] ?? 0) - recoil);
        event.recoil = recoil;
      }
    }
    // Hitting a wall is tiring, and it leaves the thrower open: the defender's turn.
    attacker.stamina = Math.max(
      0,
      attacker.stamina - strikeCost(strike.limb, strike.charge, strike.lead) * g.attackerJolt,
    );
    strike.recoveryTicks += g.blockedRecovery;
    attacker.initiativeLock =
      strike.throwTicks - strike.ticks + strike.recoveryTicks + tuning.initiativeTicks;
  }

  damage = Math.round(damage * 10) / 10;
  event.damage = damage;
  target.hp[vol.part] = Math.max(0, (target.hp[vol.part] ?? 0) - damage);
  target.lastHitBy = attacker.id;
  attacker.damageDealt += damage;
  // A heavier build takes more moving.
  const shove =
    (mEff * closing * tuning.shoveScale * impulseFraction) /
    (tuning.bodyMass * target.build.strength);
  target.impulse = add(target.impulse, scale(n, -shove));
  events.push(event);
  return true;
}
